<template>
    <b-modal 
        id="add-new-diagnostic-image-category-sidebar" 
        :visible="isEditReviewSidebarActive" 
        size="xl"
        @hidden="resetForm" 
        title="Edit Membership Parameter" 
        hide-footer
        @change="(val) => $emit('update:isEditReviewSidebarActive', val)">
        <template #default="{ hide }">
            <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
                <b-form class="p-3" @submit.prevent="handleSubmit(onSubmit)" @reset.prevent="resetForm">
                    <b-alert v-if="formError" show variant="danger" class="mb-3">{{ formError }}</b-alert>
                    <!-- Sección: Información Básica -->
                    <b-card class="mb-3 border-0 shadow-sm" no-body>
                        <b-card-header class="bg-light border-bottom">
                            <h6 class="mb-0 text-primary">
                                <i class="fas fa-info-circle mr-2"></i>
                                Basic Information
                            </h6>
                        </b-card-header>
                        <b-card-body>
                            <validation-provider #default="validationContext" name="name" rules="required">
                                <b-form-group label="Name" label-for="name">
                                    <b-form-input 
                                        id="name" 
                                        v-model="stateData.name" 
                                        autofocus
                                        :state="getValidationState(validationContext)" 
                                        trim
                                        placeholder="Enter membership parameter name" />
                                    <b-form-invalid-feedback>
                                        {{ validationContext.errors[0] }}
                                    </b-form-invalid-feedback>
                                </b-form-group>
                            </validation-provider>

                            <b-row>
                                <b-col cols="12" md="4">
                                    <validation-provider #default="validationContext" name="validity" rules="required|numeric|max_value:12">
                                        <b-form-group label="Validity (months)" label-for="validity">
                                            <b-form-input 
                                                id="validity" 
                                                v-model="stateData.validity"
                                                :state="getValidationState(validationContext)" 
                                                trim 
                                                type="number"
                                                min="1"
                                                max="12"
                                                placeholder="1-12 months" />
                                            <b-form-invalid-feedback>
                                                {{ validationContext.errors[0] }}
                                            </b-form-invalid-feedback>
                                        </b-form-group>
                                    </validation-provider>
                                </b-col>
                                <b-col cols="12" md="4">
                                    <validation-provider #default="validationContext" name="membership_type" rules="required">
                                        <b-form-group label="Membership Type" label-for="membership_type">
                                            <treeselect 
                                                id="membership_type" 
                                                v-model="stateData.membership_type"
                                                :options="membershipTypeOptions" 
                                                :clearable="false" />
                                            <b-form-invalid-feedback>
                                                {{ validationContext.errors[0] }}
                                            </b-form-invalid-feedback>
                                        </b-form-group>
                                    </validation-provider>
                                </b-col>
                                <b-col cols="12" md="4">
                                    <validation-provider #default="validationContext" name="group_membership" rules="required">
                                        <b-form-group label="Group Membership" label-for="group_membership">
                                            <treeselect 
                                                id="group_membership" 
                                                v-model="stateData.group_membership"
                                                :options="groupMembershipOptions" 
                                                :clearable="false" />
                                            <b-form-invalid-feedback>
                                                {{ validationContext.errors[0] }}
                                            </b-form-invalid-feedback>
                                        </b-form-group>
                                    </validation-provider>
                                </b-col>
                            </b-row>

                            <validation-provider #default="validationContext" name="price" rules="required">
                                <b-form-group label="Price" label-for="price">
                                    <b-form-input 
                                        id="price" 
                                        v-model="stateData.price"
                                        :state="getValidationState(validationContext)" 
                                        trim 
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        placeholder="Enter price" />
                                    <b-form-invalid-feedback>
                                        {{ validationContext.errors[0] }}
                                    </b-form-invalid-feedback>
                                </b-form-group>
                            </validation-provider>

                            <b-row>
                                <b-col cols="12" md="6">
                                    <b-form-group label="Quarter price multiplier" label-for="edit-quarter-multiplier" description="Vacío = no se ofrece trimestral. Trimestral = mensual × este valor.">
                                        <b-form-input id="edit-quarter-multiplier" v-model="stateData.quarter_price_multiplier" type="number" min="0" step="0.01" placeholder="2.75" />
                                    </b-form-group>
                                </b-col>
                                <b-col cols="12" md="6">
                                    <b-form-group label="Year price multiplier" label-for="edit-year-multiplier" description="Anual = mensual × este valor.">
                                        <b-form-input id="edit-year-multiplier" v-model="stateData.year_price_multiplier" type="number" min="0" step="0.01" placeholder="10" />
                                    </b-form-group>
                                </b-col>
                                <b-col cols="12" md="4">
                                    <b-form-group label="Enrollment fee" label-for="edit-enrollment-fee">
                                        <b-form-input id="edit-enrollment-fee" v-model="stateData.enrollment_fee" type="number" min="0" step="0.01" placeholder="0" />
                                    </b-form-group>
                                </b-col>
                                <b-col cols="12" md="4">
                                    <b-form-group label="Reactivation fee" label-for="edit-reactivation-fee">
                                        <b-form-input id="edit-reactivation-fee" v-model="stateData.reactivation_fee" type="number" min="0" step="0.01" placeholder="25" />
                                    </b-form-group>
                                </b-col>
                                <b-col cols="12" md="4">
                                    <b-form-group label="Uncovered discount %" label-for="edit-uncovered-discount" description="Vacío = no cambia el porcentaje guardado.">
                                        <b-form-input id="edit-uncovered-discount" v-model="stateData.uncovered_discount_percentage" type="number" min="0" max="100" step="1" />
                                    </b-form-group>
                                </b-col>
                            </b-row>

                            <b-row class="mt-2">
                                <b-col cols="12" md="4">
                                    <b-form-group label="Plan kind" label-for="edit-plan-kind" description="Familiar usa tramos por cantidad de personas.">
                                        <b-form-select id="edit-plan-kind" v-model="stateData.plan_kind" :options="planKindOptions" />
                                    </b-form-group>
                                </b-col>
                            </b-row>
                            <template v-if="stateData.plan_kind === 'family'">
                                <b-row>
                                    <b-col cols="12" md="3">
                                        <b-form-group label="Minimum members" label-for="edit-min-members">
                                            <b-form-input id="edit-min-members" v-model="stateData.min_members" type="number" min="2" />
                                        </b-form-group>
                                    </b-col>
                                    <b-col cols="12" md="3">
                                        <b-form-group label="Child minimum age" label-for="edit-child-age">
                                            <b-form-input id="edit-child-age" v-model="stateData.child_min_age" type="number" min="0" />
                                        </b-form-group>
                                    </b-col>
                                    <b-col cols="12" md="3">
                                        <b-form-group label="Adult minimum age" label-for="edit-adult-age">
                                            <b-form-input id="edit-adult-age" v-model="stateData.adult_min_age" type="number" min="0" />
                                        </b-form-group>
                                    </b-col>
                                    <b-col cols="12" md="3">
                                        <b-form-group label="Extra member amount" label-for="edit-extra-member">
                                            <b-form-input id="edit-extra-member" v-model="stateData.extra_member_amount" type="number" min="0" step="0.01" />
                                        </b-form-group>
                                    </b-col>
                                </b-row>
                                <div class="mb-2">
                                    <div class="d-flex justify-content-between align-items-center mb-2">
                                        <span class="text-primary">Family tiers</span>
                                        <b-button size="sm" variant="outline-primary" type="button" @click="addFamilyTier">Add tier</b-button>
                                    </div>
                                    <small v-if="!stateData.family_tiers || !stateData.family_tiers.length" class="d-block text-muted mb-2">
                                        Leave empty to keep the current tiers. Writing tiers replaces all of them.
                                    </small>
                                    <b-row v-for="(tier, tierIndex) in stateData.family_tiers" :key="'edit-tier-' + tierIndex" class="mb-2">
                                        <b-col cols="12" md="4">
                                            <b-form-input v-model="tier.members" type="number" min="2" placeholder="Members" />
                                        </b-col>
                                        <b-col cols="12" md="4">
                                            <b-form-input v-model="tier.amount" type="number" min="0" step="0.01" placeholder="Amount" />
                                        </b-col>
                                        <b-col cols="12" md="4">
                                            <b-button size="sm" variant="outline-danger" type="button" @click="removeFamilyTier(tierIndex)">Remove</b-button>
                                        </b-col>
                                    </b-row>
                                </div>
                            </template>
                        </b-card-body>
                    </b-card>

                    <!-- Sección: Configuración -->
                    <b-card class="mb-3 border-0 shadow-sm" no-body>
                        <b-card-header class="bg-light border-bottom">
                            <h6 class="mb-0 text-primary">
                                <i class="fas fa-cog mr-2"></i>
                                Configuration
                            </h6>
                        </b-card-header>
                        <b-card-body>
                            <validation-provider #default="validationContext" name="facility" rules="required">
                                <b-form-group label="Facilities" label-for="facility">
                                    <treeselect 
                                        id="facility" 
                                        v-model="facilities" 
                                        :options="facilityOptionsWithSelectAll" 
                                        :multiple="true"
                                        :clearable="true" 
                                        :normalizer="normalizer" 
                                        @input="handleFacilityChange" />
                                    <b-form-invalid-feedback>
                                        {{ validationContext.errors[0] }}
                                    </b-form-invalid-feedback>
                                </b-form-group>
                            </validation-provider>

                            <validation-provider #default="validationContext" name="Status" rules="required">
                                <b-form-group :label="$t('Form.Status')" label-for="status">
                                    <treeselect 
                                        id="status" 
                                        v-model="stateData.status"
                                        :state="getValidationState(validationContext)" 
                                        :options="optionsStatus"
                                        :normalizer="normalizer" />
                                    <b-form-invalid-feedback>
                                        {{ validationContext.errors[0] }}
                                    </b-form-invalid-feedback>
                                </b-form-group>
                            </validation-provider>

                            <!-- Group Membership Fields -->
                            <template v-if="stateData.group_membership === true">
                                <b-row>
                                    <b-col cols="12" md="6">
                                        <validation-provider #default="validationContext" name="adults_count" :rules="stateData.group_membership ? 'required|numeric|min_value:0' : ''">
                                            <b-form-group label="Adults" label-for="adults_count">
                                                <b-form-input 
                                                    id="adults_count" 
                                                    v-model="stateData.adults_count"
                                                    :state="getValidationState(validationContext)" 
                                                    trim 
                                                    type="number"
                                                    min="0"
                                                    placeholder="Enter number of adults" />
                                                <b-form-invalid-feedback>
                                                    {{ validationContext.errors[0] }}
                                                </b-form-invalid-feedback>
                                            </b-form-group>
                                        </validation-provider>
                                    </b-col>
                                    <b-col cols="12" md="6">
                                        <validation-provider #default="validationContext" name="minors_count" :rules="stateData.group_membership ? 'required|numeric|min_value:0' : ''">
                                            <b-form-group label="Minors" label-for="minors_count">
                                                <b-form-input 
                                                    id="minors_count" 
                                                    v-model="stateData.minors_count"
                                                    :state="getValidationState(validationContext)" 
                                                    trim 
                                                    type="number"
                                                    min="0"
                                                    placeholder="Enter number of minors" />
                                                <b-form-invalid-feedback>
                                                    {{ validationContext.errors[0] }}
                                                </b-form-invalid-feedback>
                                            </b-form-group>
                                        </validation-provider>
                                    </b-col>
                                </b-row>
                            </template>
                        </b-card-body>
                    </b-card>

                    <!-- Sección: Parámetros -->
                    <b-card class="mb-3 border-0 shadow-sm" no-body>
                        <b-card-header class="bg-light border-bottom d-flex justify-content-between align-items-center">
                            <h6 class="mb-0 text-primary">
                                <i class="fas fa-list-ul mr-2"></i>
                                Membership Parameters
                            </h6>
                            <small class="text-muted">{{ parameters.length }} parameter(s)</small>
                        </b-card-header>
                        <b-card-body>
                            <div 
                                v-for="(parameter, index) in parameters" 
                                :key="index"
                                class="parameter-item mb-3 p-3 border rounded"
                                :class="{ 'bg-light': index % 2 === 0 }">
                                <div class="d-flex justify-content-between align-items-center mb-2">
                                    <h6 class="mb-0 text-muted">Parameter #{{ index + 1 }}</h6>
                                    <b-button 
                                        v-if="index !== 0"
                                        variant="outline-danger" 
                                        size="sm"
                                        @click="removeParameter(index)"
                                        class="parameter-remove-btn">
                                        <i class="fas fa-trash-alt"></i>
                                        <span class="ml-1 d-none d-sm-inline">Delete</span>
                                    </b-button>
                                </div>
                                
                                <b-row>
                                    <b-col cols="12" md="6" lg="3">
                                        <validation-provider #default="validationContext" :name="`parameter_type_${index}`" rules="required">
                                            <b-form-group label="Parameter Type" :label-for="`parameter_type_${index}`">
                                                <treeselect 
                                                    v-model="parameter.type" 
                                                    :async="true" 
                                                    :options="options"
                                                    :defaultOptions="true" 
                                                    :load-options="searchMemberships" 
                                                    search-nested
                                                    :disable-branch-nodes="true" 
                                                    valueFormat="object"
                                                    placeholder="Select type" />
                                                <b-form-invalid-feedback>
                                                    {{ validationContext.errors[0] }}
                                                </b-form-invalid-feedback>
                                            </b-form-group>
                                        </validation-provider>
                                    </b-col>

                                    <b-col cols="12" md="6" lg="2">
                                        <validation-provider #default="validationContext" :name="`count_${index}`" rules="required">
                                            <b-form-group label="Quantity" :label-for="`count_${index}`">
                                                <b-form-input 
                                                    v-model="parameter.count" 
                                                    :state="getValidationState(validationContext)"
                                                    trim 
                                                    type="number"
                                                    min="1"
                                                    placeholder="Quantity" />
                                                <b-form-invalid-feedback>
                                                    {{ validationContext.errors[0] }}
                                                </b-form-invalid-feedback>
                                            </b-form-group>
                                        </validation-provider>
                                    </b-col>

                                    <b-col cols="12" md="6" lg="2">
                                        <validation-provider #default="validationContext" :name="`activated_at_${index}`" rules="required">
                                            <b-form-group label="Activated at (month)" :label-for="`activated_at_${index}`">
                                                <treeselect 
                                                    v-model="parameter.activated_at"
                                                    :disabled="!stateData.validity || stateData.validity === null"
                                                    :options="months"
                                                    placeholder="Month" />
                                                <b-form-invalid-feedback>
                                                    {{ validationContext.errors[0] }}
                                                </b-form-invalid-feedback>
                                                <small v-if="!stateData.validity || stateData.validity === null" class="text-muted">
                                                    First define validity
                                                </small>
                                            </b-form-group>
                                        </validation-provider>
                                    </b-col>

                                    <b-col cols="12" md="6" lg="3">
                                        <validation-provider #default="validationContext" :name="`discount_percentage_${index}`" :rules="{ min_value: 0, max_value: 100, numeric: true }">
                                            <b-form-group label="Discount (%)" :label-for="`discount_percentage_${index}`">
                                                <b-form-input 
                                                    v-model.number="parameter.discount_percentage" 
                                                    :state="getValidationState(validationContext)"
                                                    trim 
                                                    type="number" 
                                                    min="0" 
                                                    max="100" 
                                                    step="0.01"
                                                    placeholder="0-100" />
                                                <b-form-invalid-feedback>
                                                    {{ validationContext.errors[0] || 'Percentage must be between 0 and 100' }}
                                                </b-form-invalid-feedback>
                                            </b-form-group>
                                        </validation-provider>
                                    </b-col>

                                    <b-col cols="12" md="6" lg="2">
                                        <b-form-group label="Audience" :label-for="`edit_member_type_${index}`" description="Vacío = adulto y niño.">
                                            <b-form-select :id="`edit_member_type_${index}`" v-model="parameter.member_type" :options="benefitAudienceOptions" />
                                        </b-form-group>
                                    </b-col>

                                    <b-col cols="12" lg="2" class="d-flex align-items-end">
                                        <b-button 
                                            v-if="index === parameters.length - 1"
                                            variant="outline-primary" 
                                            size="sm"
                                            @click="addParameter"
                                            class="w-100 parameter-add-btn">
                                            <i class="fas fa-plus mr-1"></i>
                                            Add
                                        </b-button>
                                    </b-col>
                                </b-row>
                            </div>
                        </b-card-body>
                    </b-card>

                    <!-- Form Actions -->
                    <div class="d-flex justify-content-end mt-4 pt-3 border-top">
                        <b-button 
                            type="button" 
                            variant="outline-secondary" 
                            class="mr-2"
                            @click="hide"
                            :disabled="loading">
                            {{ $t("Cancel") }}
                        </b-button>
                        <b-button 
                            variant="primary" 
                            type="submit" 
                            :disabled="loading">
                            <span v-if="!loading">
                                <i class="fas fa-save mr-1"></i>
                                Save Changes
                            </span>
                            <span v-else>
                                <SpinnerLoading />
                            </span>
                        </b-button>
                    </div>
                </b-form>
            </validation-observer>
        </template>
    </b-modal>
</template>

<script>
import {
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    BRow,
    BCol,
    BCard,
    BCardHeader,
    BCardBody,
    BAlert,
} from "bootstrap-vue";
import formValidation from "@/core/utils/forms/form-validation";
import { LOAD_CHILDREN_OPTIONS } from '@riophae/vue-treeselect'
import { ValidationProvider, ValidationObserver } from "vee-validate";
import SpinnerLoading from "@/components/SpinnerLoading";

import { required } from "@validations";
import { ref, watch, computed } from "@vue/composition-api";

import axiosCT from "@/core/services/api/admin/consults/memberships";
import { applyFamilyPlanFields, applyMembershipPricingFields, BENEFIT_AUDIENCE_OPTIONS, membershipApiMessage, PLAN_KIND_OPTIONS } from "@/core/utils/membershipAdmin";

/**
 * Component for editing existing membership parameters.
 * Allows modifying basic information, facilities and dynamic parameters.
 */

export default {
    components: {
        BModal,
        BForm,
        BFormGroup,
        BFormInput,
        BFormInvalidFeedback,
        BButton,
        BRow,
        BCol,
        BCard,
        BCardHeader,
        BCardBody,
        BAlert,
        SpinnerLoading,

        // Form Validation
        ValidationProvider,
        ValidationObserver,
    },
    model: {
        prop: "isEditReviewSidebarActive",
        event: "update:is-edit-system-review-sidebar-active",
    },
    props: {
        isEditReviewSidebarActive: {
            type: Boolean,
            required: true,
        },
        itemEdit: {
            type: Object,
            required: true,
            default: () => ({}),
        },
    },
    data() {
        return {
            required,
            normalizer(node) {
                return {
                    id: node.id,
                    label: node.name,
                };
            },
        };
    },
    setup(props, { emit }) {
        const optionsStatus = [
            { name: "Active", id: 1 },
            { name: "Inactive", id: 0 },
        ];
        const membershipTypeOptions = [
            { id: 0, label: "Membership" },
            { id: 1, label: "Medical Plan" },
        ];
        const groupMembershipOptions = [
            { id: false, label: "Individual" },
            { id: true, label: "Group" },
        ];

        // Estado del formulario
        const months = ref([])
        const loading = ref(false)
        const formError = ref(null)
        
        // Opciones para búsqueda de parámetros
        const parameterTypeOptions = ref([])
        const laboratories = ref([])
        const laboratoryCategories = ref([])
        const procedures = ref([])
        const sonograms = ref([])
        const tests = ref([])
        const treatments = ref([])

        const facilityOptions = JSON.parse(atob(localStorage.getItem("setFacilityOptions")));
        const SELECT_ALL_ID = "select-all-facilities";
        
        const facilityOptionsWithSelectAll = computed(() => {
            const selectAllOption = {
                id: SELECT_ALL_ID,
                name: "Select All",
            };
            return [selectAllOption, ...facilityOptions];
        });

        const facilityOptionIds = () =>
            facilityOptions
                .map(option => option.id)
                .filter(id => id !== undefined && id !== null);

        const handleFacilityChange = (selectedValues) => {
            if (Array.isArray(selectedValues)) {
                const hasSelectAll = selectedValues.includes(SELECT_ALL_ID);
                const ids = facilityOptionIds();
                
                if (hasSelectAll) {
                    // Si se seleccionó "Seleccionar todo", seleccionar solo todas las facilidades (sin el SELECT_ALL_ID)
                    facilities.value = ids;
                } else {
                    // Si se deseleccionó "Seleccionar todo" o se deseleccionó alguna facilidad
                    facilities.value = selectedValues.filter(val => val !== SELECT_ALL_ID);
                }
            } else {
                if (selectedValues === SELECT_ALL_ID) {
                    facilities.value = facilityOptionIds();
                } else {
                    facilities.value = [selectedValues];
                }
            }
        };

        const stateData = ref(JSON.parse(JSON.stringify(props.itemEdit.value)));
        // Ensure group_membership has a default value if not from backend
        if (stateData.value.group_membership === undefined || stateData.value.group_membership === null) {
            stateData.value.group_membership = false;
        }
        
        // Initialize adults and minors fields
        if (stateData.value.adults_count === undefined || stateData.value.adults_count === null) {
            stateData.value.adults_count = 0;
        }
        if (stateData.value.minors_count === undefined || stateData.value.minors_count === null) {
            stateData.value.minors_count = 0;
        }
        if (!stateData.value.plan_kind) stateData.value.plan_kind = 'individual';
        if (!Array.isArray(stateData.value.family_tiers)) {
            const rawTiers = stateData.value.family_tiers
            stateData.value.family_tiers = rawTiers && Array.isArray(rawTiers.tiers) ? rawTiers.tiers : []
        }
        
        // If there are parameters_groups, calculate adults and minors counts
        if (stateData.value.parameters_groups && Array.isArray(stateData.value.parameters_groups) && stateData.value.parameters_groups.length > 0) {
            const adultsCount = stateData.value.parameters_groups.filter(g => g.type === 1).length;
            const minorsCount = stateData.value.parameters_groups.filter(g => g.type === 2).length;
            stateData.value.adults_count = adultsCount;
            stateData.value.minors_count = minorsCount;
        }

        // Inicializar parameters siempre como array
        const parameters = ref([]);
        // Si hay parameters_details, se llenará más adelante, si no, se inicializa con un elemento por defecto
        if (!stateData.value.parameters_details || !Array.isArray(stateData.value.parameters_details) || stateData.value.parameters_details.length === 0) {
            parameters.value = [{ type: null, count: null, activated_at: null, discount_percentage: 0, member_type: '' }];
        }

        const facilities = ref(stateData.value.facilities || [])

        /**
         * Transforms an array of data into tree structure for treeselect.
         * @param {Object} params - Transformation parameters
         * @param {Array} params.array - Array of elements to transform
         * @param {string} params.subChildren - Name of the property containing sub-elements
         * @param {number|string} params.parentId - Parent element ID
         * @returns {Array} Transformed array for treeselect
         */
        const transformToTreeSelectOptions = ({ array, subChildren, parentId }) => {
            return array.map((child) => {
                if (subChildren && child[subChildren]) {
                    const subChildrenOptions = child[subChildren].map(subChild => ({
                        id: `${subChild.id} - ${subChild.name} - ${parentId}`,
                        label: subChild.price ? `${subChild.name} - ${subChild.price}` : subChild.name,
                        trueId: subChild.id,
                        fatherId: parentId
                    }));
                    
                    return {
                        id: `${child.id} - ${child.name} - ${parentId}`,
                        label: child.name,
                        children: subChildrenOptions,
                        trueId: child.id,
                        fatherId: parentId
                    };
                }
                
                return {
                    id: `${child.id} - ${child.name} - ${parentId}`,
                    label: child.price ? `${child.name} - ${child.price}` : child.name,
                    trueId: child.id,
                    fatherId: parentId
                };
            });
        }

        const resetuserData = () => {
            emit("editReviewSuccess", false);
            stateData.value = JSON.parse(JSON.stringify(props.itemEdit.value));
            
            // Reinitialize adults and minors fields
            if (stateData.value.adults_count === undefined || stateData.value.adults_count === null) {
                stateData.value.adults_count = 0;
            }
            if (stateData.value.minors_count === undefined || stateData.value.minors_count === null) {
                stateData.value.minors_count = 0;
            }
            
            // If there are parameters_groups, calculate adults and minors counts
            if (stateData.value.parameters_groups && Array.isArray(stateData.value.parameters_groups) && stateData.value.parameters_groups.length > 0) {
                const adultsCount = stateData.value.parameters_groups.filter(g => g.type === 1).length;
                const minorsCount = stateData.value.parameters_groups.filter(g => g.type === 2).length;
                stateData.value.adults_count = adultsCount;
                stateData.value.minors_count = minorsCount;
            }
        };

        /**
         * Searches and loads parameter type options for treeselect.
         * Handles asynchronous search and child loading by type.
         */
        const searchMemberships = ({ action, searchQuery, callback, parentNode }) => {
            if (action === "ASYNC_SEARCH") {
                axiosCT.typeParameterSearch()
                    .then(({ data: { data } }) => {
                        const transformedData = data.map((res) => {
                            if (res.identifier === "consultations") {
                                return {
                                    id: res.id,
                                    label: res.name,
                                    identifier: res.identifier,
                                };
                            }
                            return {
                                id: res.id,
                                label: res.name,
                                identifier: res.identifier,
                                children: null
                            };
                        });
                        parameterTypeOptions.value = transformedData;
                        callback(null, transformedData);
                    })
                    .catch((error) => {
                        console.error('Error al buscar tipos de parámetros:', error);
                        callback(error);
                    });
                return;
            }

            if (action === LOAD_CHILDREN_OPTIONS) {
                try {
                    const identifierMap = {
                        "procedures": { array: procedures.value },
                        "laboratories": { array: laboratories.value, subChildren: "laboratories" },
                        "laboratories_pack": { array: laboratoryCategories.value },
                        "test": { array: tests.value },
                        "diagnostic_imagings": { array: sonograms.value, subChildren: "diagnostic_imagings" },
                        "treatments": { array: treatments.value }
                    };

                    const config = identifierMap[parentNode.identifier];
                    if (!config) {
                        callback(new Error('Tipo de parámetro no encontrado'));
                        return;
                    }

                    parentNode.children = transformToTreeSelectOptions({
                        array: config.array,
                        subChildren: config.subChildren,
                        parentId: parentNode.id
                    });
                    callback();
                } catch (error) {
                    console.error('Error al cargar opciones hijas:', error);
                    callback(new Error('Error al cargar opciones hijas'));
                }
            }
        }

        /**
         * Loads parameterizable data from the backend.
         * @param {number} page - Page number (default 1)
         */
        const loadParametrizables = (page = 1) => {
            axiosCT.mptParametrizables(page)
                .then(({ 
                    category_lab, 
                    procedures: { data: procedure_data }, 
                    sonograms: sonogramsData, 
                    tests: { data: test_data }, 
                    treatments: treatmentsData 
                }) => {
                    laboratories.value = category_lab;
                    laboratoryCategories.value = category_lab.filter(lab => lab.price);
                    procedures.value = procedure_data;
                    sonograms.value = sonogramsData;
                    tests.value = test_data;
                    treatments.value = treatmentsData;
                })
                .catch((error) => {
                    console.error('Error al cargar parametrizables:', error);
                });
        }

        loadParametrizables();

        /**
         * Gets the formatted label for a parameterizable parameter.
         * @param {Object} parameter - Parameter object
         * @returns {string} Formatted label
         */
        const getParametrizableLabel = (parameter) => {
            if (!parameter.parametrizable) return "";
            if (!parameter.parametrizable.price) return parameter.parametrizable.name;
            return `${parameter.parametrizable.name} - ${parameter.parametrizable.price}`;
        }

        // Only process parameters_details if it exists and is an array with elements
        if (stateData.value.parameters_details && Array.isArray(stateData.value.parameters_details) && stateData.value.parameters_details.length > 0) {
            stateData.value.parameters_details.forEach(parameter => {
                // Validate that parameter is a valid object
                if (!parameter || typeof parameter !== 'object') {
                    return;
                }
                
                if (parameter.memberships_parameters_types_id == 1) {
                    parameters.value.push({
                        type: {
                            id: parameter.memberships_parameters_types_id,
                            label: parameter.memberships_parameters_types?.name || '',
                        },
                        count: parameter.count ?? null,
                        activated_at: parameter.activated_at ?? null,
                        discount_percentage: parameter.discount_percentage ?? 0,
                        member_type: parameter.member_type || '',
                        detailId: parameter.id ?? null,
                    })
                } else {
                    const label = getParametrizableLabel(parameter);
                    parameters.value.push({
                        type: {
                            id: `${parameter.parametrizable_id || ''} - ${label}`,
                            label: label,
                            trueId: parameter.parametrizable_id,
                            fatherId: parameter.memberships_parameters_types_id
                        },
                        count: parameter.count ?? null,
                        activated_at: parameter.activated_at ?? null,
                        discount_percentage: parameter.discount_percentage ?? 0,
                        member_type: parameter.member_type || '',
                        detailId: parameter.id ?? null,
                    });
                }
            });
        }

        /**
         * Generates month options based on parameter validity.
         * Updates the array of available months for selection.
         */
        const updateMonthsOptions = () => {
            const validity = stateData.value.validity;
            if (!validity || validity > 12 || validity < 1) {
                months.value = [];
                return;
            }
            
            months.value = Array.from({ length: validity }, (_, i) => ({
                id: `${i}`,
                label: `Month ${i + 1}`
            }));
        }

        // Observar cambios en la validez para actualizar meses
        watch(() => stateData.value.validity, () => {
            updateMonthsOptions();
        });

        // Inicializar meses al cargar
        updateMonthsOptions();

        /**
         * Adds a new parameter to the form.
         */
        const addFamilyTier = () => {
            if (!Array.isArray(stateData.value.family_tiers)) stateData.value.family_tiers = [];
            stateData.value.family_tiers.push({ members: null, amount: null });
        };
        const removeFamilyTier = (index) => {
            stateData.value.family_tiers.splice(index, 1);
        };

        const addParameter = () => {
            parameters.value.push({
                type: null,
                count: null,
                activated_at: null,
                discount_percentage: 0,
                member_type: ''
            });
        };

        /**
         * Removes a parameter from the form.
         * @param {number} index - Index of the parameter to remove
         */
        const removeParameter = (index) => {
            if (parameters.value.length > 1 && index > 0) {
                parameters.value.splice(index, 1);
            }
        };

        /**
         * Transforms form parameters to the format expected by the backend.
         * @param {Array} params - Array of form parameters
         * @returns {Array} Transformed array for the backend
         */
        const transformParametersForSubmit = (params) => {
            return params.map(({ count, activated_at, discount_percentage, detailId, member_type, type }) => {
                if (!type || !type.id) {
                    throw new Error('All parameters must have a selected type');
                }

                const { id, trueId, fatherId } = type;
                const row = {
                    count,
                    activated_at,
                    discount_percentage: discount_percentage ? Number(discount_percentage) : 0,
                    member_type: member_type || null,
                    parametrizable_id: trueId,
                    memberships_parameters_types_id: id === 1 ? id : fatherId
                };
                if (detailId != null && detailId !== '') row.id = detailId
                return row
            });
        };

        /**
         * Generates the parameters_groups array based on adults and minors counts.
         * @param {number} adultsCount - Number of adults
         * @param {number} minorsCount - Number of minors
         * @returns {Array} Array of parameters_groups
         */
        const generateParametersGroups = (adultsCount, minorsCount) => {
            const groups = [];
            
            // Add adults (type: 1)
            for (let i = 0; i < adultsCount; i++) {
                groups.push({ type: 1 });
            }
            
            // Add minors (type: 2)
            for (let i = 0; i < minorsCount; i++) {
                groups.push({ type: 2 });
            }
            
            return groups;
        };

        /**
         * Handles form submission.
         * Validates, transforms and sends data to the backend.
         */
        const onSubmit = () => {
            loading.value = true;
            formError.value = null;
            
            try {
                const transformedParameters = transformParametersForSubmit(parameters.value);
                const formData = applyFamilyPlanFields(applyMembershipPricingFields({
                    ...stateData.value,
                    parameters_details: transformedParameters,
                    facilities: facilities.value,
                }));

                // If group membership, add parameters_groups
                if (formData.group_membership === true) {
                    const adultsCount = Number(formData.adults_count) || 0;
                    const minorsCount = Number(formData.minors_count) || 0;
                    formData.parameters_groups = generateParametersGroups(adultsCount, minorsCount);
                }

                // Remove auxiliary fields before sending
                delete formData.adults_count;
                delete formData.minors_count;

                axiosCT.membershipParameterUpdate(stateData.value.id, formData)
                    .then(() => {
                        loading.value = false;
                        resetuserData();
                        emit("editmembershipSuccess", true);
                    })
                    .catch((error) => {
                        console.error('Error updating membership parameter:', error);
                        formError.value = membershipApiMessage(error, 'No se pudo guardar el plan.');
                        loading.value = false;
                    });
            } catch (error) {
                console.error('Error transforming parameters:', error);
                formError.value = error && error.message ? error.message : 'No se pudo guardar el plan.';
                loading.value = false;
            }
        };
        const { refFormObserver, getValidationState, resetForm } = formValidation(
            resetuserData
        );
        return {
            // Estado
            loading,
            formError,
            stateData,
            facilities,
            parameters,
            months,
            
            // Opciones
            optionsStatus,
            membershipTypeOptions,
            groupMembershipOptions,
            planKindOptions: PLAN_KIND_OPTIONS,
            benefitAudienceOptions: BENEFIT_AUDIENCE_OPTIONS,
            facilityOptions,
            facilityOptionsWithSelectAll,
            options: parameterTypeOptions,
            
            // Métodos
            onSubmit,
            addParameter,
            removeParameter,
            addFamilyTier,
            removeFamilyTier,
            searchMemberships,
            handleFacilityChange,
            
            // Validación
            refFormObserver,
            getValidationState,
            resetForm,
        };
    },
};
</script>

<style scoped>
.parameter-item {
    transition: all 0.2s ease-in-out;
}

.parameter-item:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.parameter-remove-btn,
.parameter-add-btn {
    transition: all 0.2s ease-in-out;
}

.parameter-remove-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(220, 53, 69, 0.3);
}

.parameter-add-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 123, 255, 0.3);
}

.bg-light {
    background-color: #f8f9fa !important;
}

.shadow-sm {
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075) !important;
}
</style>
